import { useTranslation } from "react-i18next";

export const DEFAULT_INTL_LOCALE = "pt-BR";

/**
 * Converte o idioma do i18next (`pt_BR`, `en_US`, `es_MX`, ou só `pt`) numa
 * tag BCP-47 aceita pelo `Intl` (`pt-BR`...). Idioma ausente ou inválido cai
 * no pt-BR, o fallback do app.
 */
export function toIntlLocale(language?: string | null): string {
  if (!language) return DEFAULT_INTL_LOCALE;
  const tag = language.replace(/_/g, "-");
  try {
    return (
      Intl.NumberFormat.supportedLocalesOf([tag])[0] ?? DEFAULT_INTL_LOCALE
    );
  } catch {
    return DEFAULT_INTL_LOCALE;
  }
}

/** Locale `Intl` do idioma atual do i18next. */
export function useIntlLocale(): string {
  const { i18n } = useTranslation();
  return toIntlLocale(i18n?.resolvedLanguage ?? i18n?.language);
}

/** Inteiro com separador de milhar do locale (pt-BR: 1.353). */
export function formatInteger(
  value: number,
  locale: string = DEFAULT_INTL_LOCALE,
): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(
    value,
  );
}

/**
 * Percentual com até `maximumFractionDigits` casas no padrão do locale
 * (pt-BR: 0,81%). Recebe o valor já em pontos percentuais (0,81 → "0,81%").
 */
export function formatPercent(
  value: number,
  locale: string = DEFAULT_INTL_LOCALE,
  maximumFractionDigits = 2,
): string {
  return new Intl.NumberFormat(locale, {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(value / 100);
}

/** Nome do mês (1-12) por extenso no locale, ex.: pt-BR 10 → "outubro". */
export function formatMonthName(
  month: number,
  locale: string = DEFAULT_INTL_LOCALE,
): string {
  return new Intl.DateTimeFormat(locale, {
    month: "long",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(2000, month - 1, 1)));
}
